/**
 * Mission & Vision Section
 * Fetched from CMS API
 */
import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const MISSION_INFO_ID = 17;
const VISION_INFO_ID = 18;

const MissionVision = () => {
  const [mission, setMission] = useState(null);
  const [vision, setVision] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch mission and vision from CMS API
  useEffect(() => {
    const fetchMissionVision = async () => {
      try {
        // Fetch Mission (InfoId: 17)
        const missionResponse = await axios.post(
          `${CMS_API_URL}info/get`,
          { InfoId: MISSION_INFO_ID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (missionResponse.data?.code === 1 && Array.isArray(missionResponse.data?.data) && missionResponse.data.data.length > 0) {
          const missionData = missionResponse.data.data[0];
          if (missionData.IsDeleted === false && missionData.IsPublished === true) {
            setMission({
              Title: missionData.Title,
              Description: missionData.Description,
              Image: missionData.Image,
            });
          }
        }

        // Fetch Vision (InfoId: 18)
        const visionResponse = await axios.post(
          `${CMS_API_URL}info/get`,
          { InfoId: VISION_INFO_ID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (visionResponse.data?.code === 1 && Array.isArray(visionResponse.data?.data) && visionResponse.data.data.length > 0) {
          const visionData = visionResponse.data.data[0];
          if (visionData.IsDeleted === false && visionData.IsPublished === true) {
            setVision({
              Title: visionData.Title,
              Description: visionData.Description,
              Image: visionData.Image,
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch mission/vision content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMissionVision();
  }, []);

  // Show loading skeleton while fetching data
  if (loading) {
    return (
      <section className="py-12 md:py-8 relative bg-[#2f3951]">
        <div className="container mx-auto px-6 md:px-8 lg:px-12 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 md:gap-28 lg:gap-36">
            <div className="h-48 bg-white/10 rounded animate-pulse" />
            <div className="h-48 bg-white/10 rounded animate-pulse" />
          </div>
        </div>
      </section>
    );
  }

  // Return null if no data from API after loading
  if (!mission && !vision) {
    return null;
  }

  return (
    <section
      className="py-6 md:py-4 relative bg-cover bg-center bg-no-repeat"
    >
      {/* Overlay */}
      <div className="container mx-auto px-6 md:px-[50px] lg:px-[80px] xl:px-[110px] 2xl:px-[140px]">
        <div className="grid md:grid-cols-2 gap-5 md:gap-6 lg:gap-8 xl:gap-10">
          {/* Mission */}
          <div
            className="bg-[#ca9a37] shadow-lg flex"
            style={{ borderRadius: '0px 100px 0px 100px' }}
          >
            <div
              className="bg-white/95 backdrop-blur-sm overflow-hidden pl-6 pr-6 py-5 md:pl-8 md:pr-8 lg:pl-12 lg:pr-12 xl:pl-14 xl:pr-14 2xl:px-[72px] ml-2 mb-0 flex flex-col justify-center min-h-[160px] md:min-h-[180px] w-full"
              style={{ borderRadius: '0px 100px 0px 100px' }}
            >
              <div className="flex items-start mb-2">
                <div className="w-10 h-10 bg-white border border-[#ca9a37]/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  {(() => { const Icon = LucideIcons[mission?.Image]; return Icon ? <Icon className="w-4 h-4 text-[#ca9a37]" /> : null; })()}
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#2f3951]">{mission?.Title || 'Our Mission'}</h3>
                  <div className="w-12 h-0.5 bg-[#ca9a37] mt-2"></div>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                {mission?.Description?.replace(/<[^>]*>/g, '')?.replace(/&nbsp;/g, ' ') || 'Improving the quality of life and increasing the value of members through the provision of high-quality financial products using modern technology.'}
              </p>
            </div>
          </div>

          {/* Vision */}
          <div
            className="bg-[#ca9a37] shadow-lg flex"
            style={{ borderRadius: '0px 100px 0px 100px' }}
          >
            <div
              className="bg-white/95 backdrop-blur-sm overflow-hidden pl-6 pr-6 py-5 md:pl-8 md:pr-8 lg:pl-12 lg:pr-12 xl:pl-14 xl:pr-14 2xl:px-[72px] ml-2 mb-0 flex flex-col justify-center min-h-[160px] md:min-h-[180px] w-full"
              style={{ borderRadius: '0px 100px 0px 100px' }}
            >
              <div className="flex items-start mb-2">
                <div className="w-10 h-10 bg-white border border-[#ca9a37]/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  {(() => { const Icon = LucideIcons[vision?.Image]; return Icon ? <Icon className="w-4 h-4 text-[#ca9a37]" /> : null; })()}
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#2f3951]">{vision?.Title || 'Our Vision'}</h3>
                  <div className="w-12 h-0.5 bg-[#ca9a37] mt-2"></div>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                {vision?.Description?.replace(/<[^>]*>/g, '')?.replace(/&nbsp;/g, ' ') || 'To be a leading and exemplary SACCOS in Tanzania.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionVision;
